import React, { useEffect, useState, useRef } from 'react';
import { motion, useInView, useScroll, useSpring } from 'framer-motion';
import { ArrowLeftIcon, CalendarIcon, ClockIcon, UserIcon, ShareIcon, LinkedinIcon, FacebookIcon, LinkIcon, ChevronRightIcon, CheckCircleIcon, ArrowRightIcon, BookOpenIcon } from 'lucide-react';
import { Header } from '../components/Header';
import { Footer } from '../components/Footer';
const tableOfContents = [{
  id: 'challenge',
  label: 'Виклик'
}, {
  id: 'strategy',
  label: 'Стратегія підбору'
}, {
  id: 'process',
  label: 'Процес реалізації'
}, {
  id: 'results',
  label: 'Результати'
}, {
  id: 'lessons',
  label: 'Висновки'
}];
function ReadingProgress() {
  const {
    scrollYProgress
  } = useScroll();
  const scaleX = useSpring(scrollYProgress, {
    stiffness: 100,
    damping: 30,
    restDelta: 0.001
  });
  return <motion.div className="fixed top-16 lg:top-20 left-0 right-0 h-0.5 bg-primary-500 origin-left z-40" style={{
    scaleX
  }} data-id="element-459" />;
}
function TableOfContents({
  activeId
}: {
  activeId: string;
}) {
  return <nav aria-label="Зміст статті" data-id="element-460">
      <h3 className="text-xs font-bold text-navy-400 uppercase tracking-widest mb-4" data-id="element-461">
        Зміст
      </h3>
      <ul className="space-y-1" data-id="element-462">
        {tableOfContents.map(item => <li key={item.id} data-id="element-463">
            <a href={`#${item.id}`} className={`block px-3 py-2 text-sm rounded-lg transition-all duration-200 ${activeId === item.id ? 'text-primary-700 bg-primary-50 font-semibold border-l-2 border-primary-500' : 'text-navy-500 hover:text-navy-700 hover:bg-navy-50'}`} data-id="element-464">
              {item.label}
            </a>
          </li>)}
      </ul>
    </nav>;
}
function ShareButtons() {
  const [copied, setCopied] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(window.location.href);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };
  return <div data-id="element-465">
      <h3 className="text-xs font-bold text-navy-400 uppercase tracking-widest mb-4" data-id="element-466">
        Поділитися
      </h3>
      <div className="flex gap-2" data-id="element-467">
        <a href="#" className="w-9 h-9 rounded-lg bg-navy-100 flex items-center justify-center text-navy-500 hover:bg-[#0A66C2] hover:text-white transition-all" aria-label="Поділитися в LinkedIn" data-id="element-468">
          <LinkedinIcon className="w-4 h-4" data-id="element-469" />
        </a>
        <a href="#" className="w-9 h-9 rounded-lg bg-navy-100 flex items-center justify-center text-navy-500 hover:bg-[#1877F2] hover:text-white transition-all" aria-label="Поділитися у Facebook" data-id="element-470">
          <FacebookIcon className="w-4 h-4" data-id="element-471" />
        </a>
        <button onClick={handleCopy} className={`w-9 h-9 rounded-lg flex items-center justify-center transition-all ${copied ? 'bg-emerald-100 text-emerald-600' : 'bg-navy-100 text-navy-500 hover:bg-primary-100 hover:text-primary-600'}`} aria-label="Скопіювати посилання" data-id="element-472">
          {copied ? <CheckCircleIcon className="w-4 h-4" data-id="element-473" /> : <LinkIcon className="w-4 h-4" data-id="element-474" />}
        </button>
      </div>
    </div>;
}
function AuthorCard() {
  return <div className="p-5 rounded-2xl bg-navy-50/60 border border-navy-100" data-id="element-475">
      <div className="flex items-center gap-3 mb-3" data-id="element-476">
        <div className="w-11 h-11 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white font-bold text-sm" data-id="element-477">
          ОК
        </div>
        <div data-id="element-478">
          <div className="font-semibold text-navy-900 text-sm" data-id="element-479">
            Олена Коваленко
          </div>
          <div className="text-xs text-navy-400" data-id="element-480">Head of Recruitment</div>
        </div>
      </div>
      <p className="text-sm text-navy-500 leading-relaxed" data-id="element-481">
        10+ років у рекрутингу. Спеціалізація — IT та фінтех. Побудувала команди
        для 50+ компаній.
      </p>
    </div>;
}
function RelatedArticles() {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-80px'
  });
  return <section className="py-20 bg-navy-50/50" ref={ref} data-id="element-482">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8" data-id="element-483">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={isInView ? {
        opacity: 1,
        y: 0
      } : {}} transition={{
        duration: 0.5
      }} className="flex items-end justify-between mb-10" data-id="element-484">
          <h2 className="text-2xl sm:text-3xl font-extrabold text-navy-900" data-id="element-485">
            Схожі статті
          </h2>
          <a href="#" className="hidden sm:inline-flex items-center gap-1 text-primary-600 font-semibold text-sm hover:text-primary-700 transition-colors" data-id="element-486">
            Усі статті
            <ArrowRightIcon className="w-4 h-4" data-id="element-487" />
          </a>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-488">
          <motion.article initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="group bg-white rounded-2xl border border-navy-100 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer" data-id="element-489">
            <div className="aspect-[16/10] bg-gradient-to-br from-amber-100 to-orange-100 flex items-center justify-center" data-id="element-490">
              <div className="w-16 h-16 rounded-2xl bg-amber-500/20 flex items-center justify-center" data-id="element-491">
                <span className="text-2xl" data-id="element-492">💡</span>
              </div>
            </div>
            <div className="p-5" data-id="element-493">
              <div className="flex items-center gap-2 text-xs text-navy-400 mb-2" data-id="element-494">
                <CalendarIcon className="w-3.5 h-3.5" data-id="element-495" />
                <span data-id="element-496">8 лютого 2026</span>
                <span className="px-1.5 py-0.5 bg-amber-50 text-amber-600 rounded text-xs font-medium" data-id="element-497">
                  Стаття
                </span>
              </div>
              <h3 className="text-base font-bold text-navy-900 group-hover:text-primary-600 transition-colors leading-snug" data-id="element-498">
                5 помилок при наймі топ‑менеджерів та як їх уникнути
              </h3>
            </div>
          </motion.article>

          <motion.article initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="group bg-white rounded-2xl border border-navy-100 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer" data-id="element-499">
            <div className="aspect-[16/10] bg-gradient-to-br from-emerald-100 to-teal-100 flex items-center justify-center" data-id="element-500">
              <div className="w-16 h-16 rounded-2xl bg-emerald-500/20 flex items-center justify-center" data-id="element-501">
                <span className="text-2xl" data-id="element-502">🌍</span>
              </div>
            </div>
            <div className="p-5" data-id="element-503">
              <div className="flex items-center gap-2 text-xs text-navy-400 mb-2" data-id="element-504">
                <CalendarIcon className="w-3.5 h-3.5" data-id="element-505" />
                <span data-id="element-506">1 лютого 2026</span>
                <span className="px-1.5 py-0.5 bg-emerald-50 text-emerald-600 rounded text-xs font-medium" data-id="element-507">
                  Огляд
                </span>
              </div>
              <h3 className="text-base font-bold text-navy-900 group-hover:text-primary-600 transition-colors leading-snug" data-id="element-508">
                Ринок праці 2026: тренди та прогнози для роботодавців
              </h3>
            </div>
          </motion.article>

          <motion.article initial={{
          opacity: 0,
          y: 24
        }} animate={isInView ? {
          opacity: 1,
          y: 0
        } : {}} transition={{
          duration: 0.5,
          delay: 0.3
        }} className="group bg-white rounded-2xl border border-navy-100 overflow-hidden hover:shadow-lg transition-shadow cursor-pointer" data-id="element-509">
            <div className="aspect-[16/10] bg-gradient-to-br from-violet-100 to-purple-100 flex items-center justify-center" data-id="element-510">
              <div className="w-16 h-16 rounded-2xl bg-violet-500/20 flex items-center justify-center" data-id="element-511">
                <span className="text-2xl" data-id="element-512">🎯</span>
              </div>
            </div>
            <div className="p-5" data-id="element-513">
              <div className="flex items-center gap-2 text-xs text-navy-400 mb-2" data-id="element-514">
                <CalendarIcon className="w-3.5 h-3.5" data-id="element-515" />
                <span data-id="element-516">25 січня 2026</span>
                <span className="px-1.5 py-0.5 bg-violet-50 text-violet-600 rounded text-xs font-medium" data-id="element-517">
                  Гайд
                </span>
              </div>
              <h3 className="text-base font-bold text-navy-900 group-hover:text-primary-600 transition-colors leading-snug" data-id="element-518">
                Як скоротити time‑to‑hire на 40%: покроковий гайд
              </h3>
            </div>
          </motion.article>
        </div>
      </div>
    </section>;
}
function CtaBanner() {
  const ref = useRef<HTMLDivElement>(null);
  const isInView = useInView(ref, {
    once: true,
    margin: '-60px'
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 20
  }} animate={isInView ? {
    opacity: 1,
    y: 0
  } : {}} transition={{
    duration: 0.5
  }} className="my-16 p-8 sm:p-10 rounded-3xl bg-gradient-to-br from-navy-900 via-navy-800 to-navy-900 relative overflow-hidden" data-id="element-519">
      <div className="absolute top-0 right-0 w-64 h-64 rounded-full bg-primary-500/10 blur-3xl" data-id="element-520" />
      <div className="absolute bottom-0 left-0 w-48 h-48 rounded-full bg-primary-400/10 blur-3xl" data-id="element-521" />
      <div className="relative z-10 flex flex-col sm:flex-row items-center gap-6 sm:gap-10" data-id="element-522">
        <div className="flex-1" data-id="element-523">
          <h3 className="text-2xl sm:text-3xl font-extrabold text-white tracking-tight" data-id="element-524">
            Потрібен підбір IT‑команди?
          </h3>
          <p className="mt-2 text-navy-300 leading-relaxed" data-id="element-525">
            Ми допоможемо зібрати команду будь‑якого масштабу — від одного
            розробника до цілого відділу.
          </p>
        </div>
        <a href="#contact" className="flex-shrink-0 inline-flex items-center gap-2 px-7 py-4 text-base font-semibold text-navy-900 bg-white rounded-xl hover:bg-navy-50 transition-all shadow-lg hover:-translate-y-0.5" data-id="element-526">
          Залишити заявку
          <ArrowRightIcon className="w-4 h-4" data-id="element-527" />
        </a>
      </div>
    </motion.div>;
}
export function ArticlePage() {
  const [activeSection, setActiveSection] = useState('challenge');
  useEffect(() => {
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          setActiveSection(entry.target.id);
        }
      });
    }, {
      rootMargin: '-20% 0px -60% 0px'
    });
    tableOfContents.forEach(({
      id
    }) => {
      const el = document.getElementById(id);
      if (el) observer.observe(el);
    });
    return () => observer.disconnect();
  }, []);
  return <div className="min-h-screen w-full bg-white" data-id="element-528">
      <Header data-id="element-529" />
      <ReadingProgress data-id="element-530" />

      <main className="pt-16 lg:pt-20" data-id="element-531">
        {/* Article Hero */}
        <section className="relative bg-navy-50 overflow-hidden" data-id="element-532">
          <div className="absolute inset-0" data-id="element-533">
            <div className="absolute -top-20 -right-20 w-[400px] h-[400px] rounded-full bg-primary-200/20 blur-3xl" data-id="element-534" />
            <div className="absolute bottom-0 left-1/4 w-[300px] h-[300px] rounded-full bg-cyan-200/15 blur-3xl" data-id="element-535" />
          </div>

          <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20 lg:py-24" data-id="element-536">
            {/* Breadcrumbs */}
            <motion.nav initial={{
            opacity: 0,
            y: 10
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.4
          }} className="flex items-center gap-1.5 text-sm text-navy-400 mb-8" aria-label="Навігаційний ланцюжок" data-id="element-537">
              <a href="#" className="hover:text-primary-600 transition-colors" data-id="element-538">
                Головна
              </a>
              <ChevronRightIcon className="w-3.5 h-3.5" data-id="element-539" />
              <a href="#" className="hover:text-primary-600 transition-colors" data-id="element-540">
                Блог
              </a>
              <ChevronRightIcon className="w-3.5 h-3.5" data-id="element-541" />
              <span className="text-navy-600 font-medium" data-id="element-542">Кейс</span>
            </motion.nav>

            <div className="max-w-3xl" data-id="element-543">
              {/* Category */}
              <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4,
              delay: 0.05
            }} data-id="element-544">
                <span className="inline-flex items-center gap-1.5 px-3 py-1 text-xs font-semibold text-primary-700 bg-primary-100/80 rounded-full border border-primary-200/50" data-id="element-545">
                  <BookOpenIcon className="w-3.5 h-3.5" data-id="element-546" />
                  Кейс
                </span>
              </motion.div>

              {/* Title */}
              <motion.h1 initial={{
              opacity: 0,
              y: 15
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.5,
              delay: 0.1
            }} className="mt-5 text-3xl sm:text-4xl lg:text-5xl font-extrabold text-navy-900 tracking-tight leading-[1.12] text-balance" data-id="element-547">
                Як ми зібрали IT‑відділ із 15 людей за 6 тижнів
              </motion.h1>

              {/* Meta */}
              <motion.div initial={{
              opacity: 0,
              y: 10
            }} animate={{
              opacity: 1,
              y: 0
            }} transition={{
              duration: 0.4,
              delay: 0.2
            }} className="mt-6 flex flex-wrap items-center gap-x-5 gap-y-2 text-sm text-navy-400" data-id="element-548">
                <div className="flex items-center gap-1.5" data-id="element-549">
                  <div className="w-7 h-7 rounded-full bg-gradient-to-br from-primary-400 to-primary-600 flex items-center justify-center text-white text-[10px] font-bold" data-id="element-550">
                    ОК
                  </div>
                  <span className="text-navy-600 font-medium" data-id="element-551">
                    Олена Коваленко
                  </span>
                </div>
                <div className="flex items-center gap-1.5" data-id="element-552">
                  <CalendarIcon className="w-4 h-4" data-id="element-553" />
                  15 лютого 2026
                </div>
                <div className="flex items-center gap-1.5" data-id="element-554">
                  <ClockIcon className="w-4 h-4" data-id="element-555" />8 хв читання
                </div>
              </motion.div>
            </div>

            {/* Cover image area */}
            <motion.div initial={{
            opacity: 0,
            y: 20
          }} animate={{
            opacity: 1,
            y: 0
          }} transition={{
            duration: 0.6,
            delay: 0.3
          }} className="mt-10 aspect-[21/9] rounded-2xl bg-gradient-to-br from-primary-100 via-cyan-100 to-primary-50 border border-primary-200/30 flex items-center justify-center overflow-hidden" data-id="element-556">
              <div className="flex items-center gap-6" data-id="element-557">
                <div className="w-24 h-24 rounded-3xl bg-primary-500/15 flex items-center justify-center" data-id="element-558">
                  <span className="text-5xl" data-id="element-559">📊</span>
                </div>
                <div className="hidden sm:block" data-id="element-560">
                  <div className="text-6xl font-extrabold text-primary-600/20" data-id="element-561">
                    15
                  </div>
                  <div className="text-sm font-semibold text-primary-600/40 -mt-1" data-id="element-562">
                    спеціалістів
                  </div>
                </div>
                <div className="hidden sm:block" data-id="element-563">
                  <div className="text-6xl font-extrabold text-primary-600/20" data-id="element-564">
                    6
                  </div>
                  <div className="text-sm font-semibold text-primary-600/40 -mt-1" data-id="element-565">
                    тижнів
                  </div>
                </div>
              </div>
            </motion.div>
          </div>
        </section>

        {/* Article Content + Sidebar */}
        <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 lg:py-16" data-id="element-566">
          <div className="flex gap-12 lg:gap-16" data-id="element-567">
            {/* Main Content */}
            <article className="flex-1 min-w-0 max-w-3xl" data-id="element-568">
              {/* Lead paragraph */}
              <motion.p initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.5
            }} className="text-xl text-navy-600 leading-relaxed font-medium mb-10" data-id="element-569">
                Фінтех‑стартап FinFlow залучив раунд Series A і мав амбітні
                плани масштабування. Головна проблема — відсутність повноцінної
                технічної команди. За 6 тижнів ми закрили 15 позицій: від
                junior‑розробників до Team Lead. Ось як це вдалося.
              </motion.p>

              {/* Section: Challenge */}
              <section id="challenge" className="mb-12 scroll-mt-24" data-id="element-570">
                <h2 className="text-2xl sm:text-3xl font-extrabold text-navy-900 tracking-tight mb-5" data-id="element-571">
                  Виклик
                </h2>
                <p className="text-lg text-navy-600 leading-relaxed mb-4" data-id="element-572">
                  FinFlow — молода фінтех‑компанія, що розробляє платіжну
                  інфраструктуру для e‑commerce. Після залучення $5M інвестицій
                  перед командою постало завдання: за максимально короткий
                  термін зібрати IT‑відділ, здатний запустити продукт до кінця
                  кварталу.
                </p>
                <p className="text-lg text-navy-600 leading-relaxed mb-6" data-id="element-573">
                  Основні складнощі, з якими зіткнувся клієнт:
                </p>
                <ul className="space-y-3 mb-6" data-id="element-574">
                  <li className="flex items-start gap-3 text-lg text-navy-600" data-id="element-575">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-primary-500 flex-shrink-0" data-id="element-576" />
                    Висока конкуренція за IT‑спеціалістів на ринку України
                  </li>
                  <li className="flex items-start gap-3 text-lg text-navy-600" data-id="element-577">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-primary-500 flex-shrink-0" data-id="element-578" />
                    Потреба у вузькопрофільних фахівцях (Go, Kubernetes,
                    фінтех‑досвід)
                  </li>
                  <li className="flex items-start gap-3 text-lg text-navy-600" data-id="element-579">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-primary-500 flex-shrink-0" data-id="element-580" />
                    Стислі терміни — продукт мав вийти через 4 місяці
                  </li>
                  <li className="flex items-start gap-3 text-lg text-navy-600" data-id="element-581">
                    <span className="mt-2 w-1.5 h-1.5 rounded-full bg-primary-500 flex-shrink-0" data-id="element-582" />
                    Відсутність внутрішнього HR‑відділу та рекрутингових
                    процесів
                  </li>
                </ul>

                {/* Stats callout */}
                <div className="grid grid-cols-3 gap-4 p-6 rounded-2xl bg-navy-50/60 border border-navy-100 my-8" data-id="element-583">
                  <div className="text-center" data-id="element-584">
                    <div className="text-3xl font-extrabold text-navy-900" data-id="element-585">
                      15
                    </div>
                    <div className="text-sm text-navy-500 mt-1" data-id="element-586">позицій</div>
                  </div>
                  <div className="text-center" data-id="element-587">
                    <div className="text-3xl font-extrabold text-primary-600" data-id="element-588">
                      6
                    </div>
                    <div className="text-sm text-navy-500 mt-1" data-id="element-589">тижнів</div>
                  </div>
                  <div className="text-center" data-id="element-590">
                    <div className="text-3xl font-extrabold text-navy-900" data-id="element-591">
                      $5M
                    </div>
                    <div className="text-sm text-navy-500 mt-1" data-id="element-592">інвестицій</div>
                  </div>
                </div>
              </section>

              {/* Section: Strategy */}
              <section id="strategy" className="mb-12 scroll-mt-24" data-id="element-593">
                <h2 className="text-2xl sm:text-3xl font-extrabold text-navy-900 tracking-tight mb-5" data-id="element-594">
                  Стратегія підбору
                </h2>
                <p className="text-lg text-navy-600 leading-relaxed mb-4" data-id="element-595">
                  Ми розробили багатоканальну стратегію, яка поєднувала активний
                  хантинг, роботу з нашою базою та таргетовані кампанії у
                  професійних спільнотах.
                </p>

                <h3 className="text-xl font-bold text-navy-900 mt-8 mb-4" data-id="element-596">
                  1. Глибокий бриф та профілювання
                </h3>
                <p className="text-lg text-navy-600 leading-relaxed mb-4" data-id="element-597">
                  Перший тиждень ми присвятили детальному вивченню потреб
                  клієнта. Провели серію зустрічей з CTO та засновниками, щоб
                  зрозуміти не лише технічні вимоги, а й культуру компанії,
                  цінності та стиль роботи.
                </p>

                <blockquote className="my-8 pl-6 border-l-4 border-primary-400 py-2" data-id="element-598">
                  <p className="text-lg text-navy-700 italic leading-relaxed" data-id="element-599">
                    «Ключ до успішного масового найму — це не швидкість, а
                    точність профілювання. Коли ви чітко розумієте, кого
                    шукаєте, процес прискорюється сам собою.»
                  </p>
                  <cite className="mt-3 block text-sm text-navy-400 not-italic font-medium" data-id="element-600">
                    — Олена Коваленко, Head of Recruitment
                  </cite>
                </blockquote>

                <h3 className="text-xl font-bold text-navy-900 mt-8 mb-4" data-id="element-601">
                  2. Паралельний пошук за всіма позиціями
                </h3>
                <p className="text-lg text-navy-600 leading-relaxed mb-4" data-id="element-602">
                  Замість послідовного закриття вакансій ми запустили пошук
                  одночасно за всіма 15 позиціями. Для цього залучили команду з
                  4 рекрутерів, кожен з яких спеціалізувався на певному стеку
                  технологій.
                </p>

                <h3 className="text-xl font-bold text-navy-900 mt-8 mb-4" data-id="element-603">
                  3. Employer branding для стартапу
                </h3>
                <p className="text-lg text-navy-600 leading-relaxed" data-id="element-604">
                  Ми допомогли FinFlow сформувати привабливу EVP (Employee Value
                  Proposition): акцент на інноваційності продукту, можливості
                  впливати на архітектуру з перших днів, гнучкий графік та
                  опціони для ранніх співробітників.
                </p>
              </section>

              {/* Section: Process */}
              <section id="process" className="mb-12 scroll-mt-24" data-id="element-605">
                <h2 className="text-2xl sm:text-3xl font-extrabold text-navy-900 tracking-tight mb-5" data-id="element-606">
                  Процес реалізації
                </h2>
                <p className="text-lg text-navy-600 leading-relaxed mb-6" data-id="element-607">
                  Весь процес ми розбили на чіткі спринти по 2 тижні з
                  вимірюваними KPI:
                </p>

                {/* Timeline */}
                <div className="space-y-6 mb-8" data-id="element-608">
                  <div className="flex gap-4" data-id="element-609">
                    <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-primary-100 text-primary-600 flex items-center justify-center text-sm font-bold" data-id="element-610">
                      1–2
                    </div>
                    <div data-id="element-611">
                      <h4 className="font-bold text-navy-900" data-id="element-612">
                        Тижні 1–2: Запуск та перші кандидати
                      </h4>
                      <p className="mt-1 text-navy-600 leading-relaxed" data-id="element-613">
                        Бриф, профілювання, запуск пошуку. Вже на другому тижні
                        представили перших 20 кандидатів на різні позиції.
                        Закрили 3 вакансії.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4" data-id="element-614">
                    <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-primary-100 text-primary-600 flex items-center justify-center text-sm font-bold" data-id="element-615">
                      3–4
                    </div>
                    <div data-id="element-616">
                      <h4 className="font-bold text-navy-900" data-id="element-617">
                        Тижні 3–4: Основний потік
                      </h4>
                      <p className="mt-1 text-navy-600 leading-relaxed" data-id="element-618">
                        Пік активності: 45+ інтерв'ю, 8 офферів. Закрили позиції
                        Backend‑розробників, DevOps та QA. Паралельно працювали
                        над складнішими позиціями.
                      </p>
                    </div>
                  </div>
                  <div className="flex gap-4" data-id="element-619">
                    <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-primary-100 text-primary-600 flex items-center justify-center text-sm font-bold" data-id="element-620">
                      5–6
                    </div>
                    <div data-id="element-621">
                      <h4 className="font-bold text-navy-900" data-id="element-622">
                        Тижні 5–6: Фінальні позиції
                      </h4>
                      <p className="mt-1 text-navy-600 leading-relaxed" data-id="element-623">
                        Закрили останні 4 позиції, включаючи Team Lead та
                        Senior‑спеціалістів. Провели onboarding‑сесію з
                        HR‑командою клієнта.
                      </p>
                    </div>
                  </div>
                </div>

                {/* Highlight box */}
                <div className="p-6 rounded-2xl bg-primary-50 border border-primary-100" data-id="element-624">
                  <div className="flex items-start gap-3" data-id="element-625">
                    <CheckCircleIcon className="w-6 h-6 text-primary-600 flex-shrink-0 mt-0.5" data-id="element-626" />
                    <div data-id="element-627">
                      <h4 className="font-bold text-navy-900" data-id="element-628">
                        Ключовий інсайт
                      </h4>
                      <p className="mt-1 text-navy-600 leading-relaxed" data-id="element-629">
                        Паралельний запуск усіх вакансій дозволив скоротити
                        загальний термін на 40% порівняно з послідовним
                        підходом. Кандидати, які не підійшли на одну позицію,
                        часто виявлялися ідеальними для іншої.
                      </p>
                    </div>
                  </div>
                </div>
              </section>

              {/* Section: Results */}
              <section id="results" className="mb-12 scroll-mt-24" data-id="element-630">
                <h2 className="text-2xl sm:text-3xl font-extrabold text-navy-900 tracking-tight mb-5" data-id="element-631">
                  Результати
                </h2>
                <p className="text-lg text-navy-600 leading-relaxed mb-6" data-id="element-632">
                  Через 6 місяців після формування команди ми зібрали зворотний
                  зв'язок від клієнта:
                </p>

                <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-8" data-id="element-633">
                  <div className="p-5 rounded-2xl bg-emerald-50 border border-emerald-100" data-id="element-634">
                    <div className="text-3xl font-extrabold text-emerald-700" data-id="element-635">
                      15/15
                    </div>
                    <div className="mt-1 text-sm text-emerald-600 font-medium" data-id="element-636">
                      усі позиції закриті в термін
                    </div>
                  </div>
                  <div className="p-5 rounded-2xl bg-emerald-50 border border-emerald-100" data-id="element-637">
                    <div className="text-3xl font-extrabold text-emerald-700" data-id="element-638">
                      100%
                    </div>
                    <div className="mt-1 text-sm text-emerald-600 font-medium" data-id="element-639">
                      пройшли випробувальний термін
                    </div>
                  </div>
                  <div className="p-5 rounded-2xl bg-primary-50 border border-primary-100" data-id="element-640">
                    <div className="text-3xl font-extrabold text-primary-700" data-id="element-641">
                      42 дні
                    </div>
                    <div className="mt-1 text-sm text-primary-600 font-medium" data-id="element-642">
                      середній час закриття позиції
                    </div>
                  </div>
                  <div className="p-5 rounded-2xl bg-primary-50 border border-primary-100" data-id="element-643">
                    <div className="text-3xl font-extrabold text-primary-700" data-id="element-644">
                      200+
                    </div>
                    <div className="mt-1 text-sm text-primary-600 font-medium" data-id="element-645">
                      кандидатів розглянуто
                    </div>
                  </div>
                </div>

                <p className="text-lg text-navy-600 leading-relaxed" data-id="element-646">
                  Продукт FinFlow було запущено вчасно. Через рік компанія
                  залучила раунд Series B, а IT‑команда зросла до 40 людей —
                  частково завдяки рекомендаціям перших найнятих спеціалістів.
                </p>
              </section>

              {/* Section: Lessons */}
              <section id="lessons" className="mb-12 scroll-mt-24" data-id="element-647">
                <h2 className="text-2xl sm:text-3xl font-extrabold text-navy-900 tracking-tight mb-5" data-id="element-648">
                  Висновки
                </h2>
                <p className="text-lg text-navy-600 leading-relaxed mb-6" data-id="element-649">
                  Цей кейс підтвердив кілька важливих принципів масового
                  IT‑найму:
                </p>
                <ol className="space-y-4 mb-6" data-id="element-650">
                  <li className="flex items-start gap-3 text-lg text-navy-600" data-id="element-651">
                    <span className="flex-shrink-0 w-7 h-7 rounded-lg bg-navy-900 text-white flex items-center justify-center text-sm font-bold mt-0.5" data-id="element-652">
                      1
                    </span>
                    <span data-id="element-653">
                      <strong className="text-navy-900" data-id="element-654">
                        Інвестуйте час у бриф.
                      </strong>{' '}
                      Детальне профілювання на старті економить тижні на
                      подальших етапах.
                    </span>
                  </li>
                  <li className="flex items-start gap-3 text-lg text-navy-600" data-id="element-655">
                    <span className="flex-shrink-0 w-7 h-7 rounded-lg bg-navy-900 text-white flex items-center justify-center text-sm font-bold mt-0.5" data-id="element-656">
                      2
                    </span>
                    <span data-id="element-657">
                      <strong className="text-navy-900" data-id="element-658">
                        Паралельний підхід працює.
                      </strong>{' '}
                      Одночасний пошук за всіма позиціями значно ефективніший за
                      послідовний.
                    </span>
                  </li>
                  <li className="flex items-start gap-3 text-lg text-navy-600" data-id="element-659">
                    <span className="flex-shrink-0 w-7 h-7 rounded-lg bg-navy-900 text-white flex items-center justify-center text-sm font-bold mt-0.5" data-id="element-660">
                      3
                    </span>
                    <span data-id="element-661">
                      <strong className="text-navy-900" data-id="element-662">
                        EVP має значення.
                      </strong>{' '}
                      Навіть стартап без бренду може залучити топ‑таланти
                      правильним позиціонуванням.
                    </span>
                  </li>
                  <li className="flex items-start gap-3 text-lg text-navy-600" data-id="element-663">
                    <span className="flex-shrink-0 w-7 h-7 rounded-lg bg-navy-900 text-white flex items-center justify-center text-sm font-bold mt-0.5" data-id="element-664">
                      4
                    </span>
                    <span data-id="element-665">
                      <strong className="text-navy-900" data-id="element-666">
                        Спеціалізація рекрутерів.
                      </strong>{' '}
                      Розподіл за стеками технологій підвищує якість скринінгу
                      та швидкість.
                    </span>
                  </li>
                </ol>
              </section>

              {/* Tags */}
              <div className="flex flex-wrap gap-2 pt-8 border-t border-navy-100" data-id="element-667">
                <span className="px-3 py-1.5 text-xs font-medium text-navy-600 bg-navy-100 rounded-lg" data-id="element-668">
                  IT‑рекрутинг
                </span>
                <span className="px-3 py-1.5 text-xs font-medium text-navy-600 bg-navy-100 rounded-lg" data-id="element-669">
                  Масовий найм
                </span>
                <span className="px-3 py-1.5 text-xs font-medium text-navy-600 bg-navy-100 rounded-lg" data-id="element-670">
                  Фінтех
                </span>
                <span className="px-3 py-1.5 text-xs font-medium text-navy-600 bg-navy-100 rounded-lg" data-id="element-671">
                  Кейс
                </span>
                <span className="px-3 py-1.5 text-xs font-medium text-navy-600 bg-navy-100 rounded-lg" data-id="element-672">
                  Стартап
                </span>
              </div>

              {/* CTA Banner */}
              <CtaBanner data-id="element-673" />

              {/* Back link */}
              <a href="#" className="inline-flex items-center gap-2 text-sm font-semibold text-primary-600 hover:text-primary-700 transition-colors" data-id="element-674">
                <ArrowLeftIcon className="w-4 h-4" data-id="element-675" />
                Повернутися до блогу
              </a>
            </article>

            {/* Sidebar */}
            <aside className="hidden lg:block w-72 flex-shrink-0" data-id="element-676">
              <div className="sticky top-28 space-y-8" data-id="element-677">
                <TableOfContents activeId={activeSection} data-id="element-678" />
                <ShareButtons data-id="element-679" />
                <AuthorCard data-id="element-680" />
              </div>
            </aside>
          </div>
        </section>

        {/* Related Articles */}
        <RelatedArticles data-id="element-681" />
      </main>

      <Footer data-id="element-682" />
    </div>;
}